import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CongfigureAddIns from "./CongfigureAddIns";
import "./PurchaseAddInsDetails.css";

function formatDate(dateValue) {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getSubscriptionManagementStatus(subscriptionManagement) {
  if (subscriptionManagement?.isExpired) return "Expired";
  if (subscriptionManagement?.isInTrial) return "Trial Active";
  if (subscriptionManagement?.isStopped) return "Stopped";
  return "Active";
}

function getOwnershipText(addIn) {

  if (addIn?.enterpriseName) {
    return `Provided by ${addIn?.enterpriseName || "Enterprise"}`;
  }
  return "Purchased by You";
}

export default function PurchaseMyAddInsDetails({
  addIn = {},
  subscriptionManagement = null,
  showRenewButton: showRenewButtonProp = false,
  showStopButton: showStopButtonProp = false,
  subscriptionTypeDisplay = "N/A",
  frequencyDisplay = "N/A",
  effectiveFromDate = null,
  effectiveToDate = null,
  trialLabel = "",
  syncing = false,
  dateFormat = "MM/DD/YYYY",
  onBack = () => {},
  onRenew = () => {},
  onUnsubscribe = () => {},
  onCreateConfiguration = () => {},
  onEditConfiguration = () => {},
  onDeleteConfiguration = () => {},
  accessGrantedBy = null,
}) {
  const isEnterpriseGroupAccess = accessGrantedBy === "EnterpriseGroup";
  const hasEnterpriseName =
    typeof addIn?.enterpriseName === "string"
      ? addIn.enterpriseName.trim().length > 0
      : addIn?.enterpriseName !== null && addIn?.enterpriseName !== undefined;
  const shouldShowManagedSections = !isEnterpriseGroupAccess && !hasEnterpriseName;
  const [confirmUnsubscribeOpen, setConfirmUnsubscribeOpen] = useState(false);
  const [confirmRenewOpen, setConfirmRenewOpen] = useState(false);

  const configFields = Array.isArray(addIn?.configurationDefinition?.fields)
    ? addIn.configurationDefinition.fields
    : [];

  const configRecords = Array.isArray(addIn?.configurations)
    ? addIn.configurations
    : [];
  const configurationRequired = addIn?.requiresConfiguration;
  const configurationStatusLabel =
    addIn?.isConfigured || configRecords.length > 0
      ? "Configured"
      : "Configuration Required";

  const findFieldKey = (matcher, fallback) =>
    configFields.find((field) => matcher((field.userLabel || field.key || "").toLowerCase()))?.key || fallback;

  const agencyFieldKey = findFieldKey((label) => label.includes("agency"), "agencyNumber");
  const usernameFieldKey = findFieldKey((label) => label.includes("username"), "username");
  const credentialFieldKey = findFieldKey(
    (label) =>
      label.includes("password") ||
      label.includes("credential") ||
      label.includes("secret") ||
      label.includes("key"),
    "credential",
  );

  const getRecordValue = (record, key) => {
    const value = record?.values?.[key];
    if (value === null || value === undefined || value === "") return "Not Set";
    return String(value);
  };

  const configureRows = configRecords.map((record, index) => {
    const agencyName = getRecordValue(record, agencyFieldKey);
    const username = getRecordValue(record, usernameFieldKey);
    const secretValue = getRecordValue(record, credentialFieldKey);

    return {
      id: record.id || index + 1,
      configurationName: record.configurationName || `Configuration ${index + 1}`,
      agencyName:
        agencyName === "Not Set"
          ? record.configurationName || `Configuration ${index + 1}`
          : agencyName,
      username,
      password: secretValue === "Not Set" ? "Not Set" : "******",
      sourceRecord: record,
    };
  });

  // Use props from parent for button visibility (from subscription management API)
  const showRenewButton = showRenewButtonProp;
  const showUnsubscribeButton = showStopButtonProp;

  const trialMessage = useMemo(() => {
    if (addIn?.subscription?.trialMessage)
      return addIn.subscription.trialMessage;

    const trialDays = addIn?.subscription?.trialDurationDays;
    const price = addIn?.subscription?.price;
    const frequency = addIn?.subscription?.frequencyText;

    if (trialDays && price && frequency) {
      return `${trialDays} Days Trial, then $${price} ${frequency}`;
    }
    return "N/A";
  }, [addIn]);

  return (
    <Box className="my-addins-details-container">
      <Button
        startIcon={
          <ArrowBackIosNewIcon fontSize="small" className="my-addins-back-icon" />
        }
        onClick={onBack}
        className="my-addins-back-button"
      ></Button>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box className="my-addins-header-block">
            <Typography
              className="available-purchase-title"
              noWrap
            >
              {addIn.name}
            </Typography>
            <Typography
              className="purchase-subtitle my-addins-header-subtitle"
              noWrap
            >
              {addIn.vendorName}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" className="paper-card my-addins-details-card">
            <Typography variant="subtitle2" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body1" className="my-addins-section-value">
              {addIn?.addInDescription || "No description available."}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" className="paper-card my-addins-details-card">
            <Typography variant="subtitle2" color="text.secondary">
              Subscription Type
            </Typography>
            <Stack direction="row" spacing={1} className="my-addins-chip-row">
              <Chip
                size="small"
                label={addIn?.addInTypeText || "N/A"}
                className="my-addins-type-chip"
              />
              <Chip
                size="small"
                label={getOwnershipText(addIn)}
                className="my-addins-ownership-chip"
              />
            </Stack>
          </Paper>
        </Grid>

        {shouldShowManagedSections && (
          <Grid item xs={12}>
            <Paper variant="outlined" className="paper-card my-addins-details-card">
              <Typography variant="h6">Configuration</Typography>

              {!configurationRequired && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  No configuration required for this add-in.
                </Typography>
              )}

              {configurationRequired && (
                <Box sx={{ mt: 2 }}>
                  <CongfigureAddIns
                    name={addIn?.name || "AMS360"}
                    subtitle="Internal Configuration"
                    status={configurationStatusLabel}
                    configurationFields={configFields}
                    rows={configureRows}
                    onAddConfiguration={onCreateConfiguration}
                    onEditConfiguration={(row) => onEditConfiguration(row.sourceRecord || row)}
                    onDeleteConfiguration={(row) => onDeleteConfiguration(row.sourceRecord || row)}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {shouldShowManagedSections && (
          <Grid item xs={12}>
            <Paper variant="outlined" className="paper-card my-addins-details-card">
              <Typography variant="h6">Subscription Management</Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body2">
                  {getSubscriptionManagementStatus(subscriptionManagement)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Subscription Type
                </Typography>
                <Typography variant="body2">
                  {subscriptionTypeDisplay}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Subscription Frequency
                </Typography>
                <Typography variant="body2">
                  {frequencyDisplay}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Trial Message
                </Typography>
                <Typography variant="body2">{trialMessage}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Effective From Date {trialLabel && `(${trialLabel})`}
                </Typography>
                <Typography variant="body2">
                  {effectiveFromDate?.format?.(dateFormat) || formatDate(subscriptionManagement?.effectiveFromDate)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Effective To Date {trialLabel && `(${trialLabel})`}
                </Typography>
                <Typography variant="body2">
                  {effectiveToDate?.format?.(dateFormat) || formatDate(subscriptionManagement?.effectiveToDate)}
                </Typography>
              </Grid>
            </Grid>

              <Stack direction="row" spacing={1} className="my-addins-subscription-actions">
                {showRenewButton && (
                  <Button
                    fullWidth
                    variant="contained"
                    className="mobile-primary-button"
                    onClick={() => setConfirmRenewOpen(true)}
                    disabled={syncing}
                  >
                    {syncing ? <CircularProgress size={20} /> : "Renew Subscription"}
                  </Button>
                )}
                {showUnsubscribeButton && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    className="unsubscribe-button"
                    onClick={() => setConfirmUnsubscribeOpen(true)}
                    disabled={syncing}
                  >
                    {syncing ? <CircularProgress size={20} /> : "Unsubscribe"}
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={confirmUnsubscribeOpen}
        onClose={() => setConfirmUnsubscribeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>Stop Subscription</DialogTitle>
        <DialogContent sx={{ marginTop: 2, textAlign: "center" }}>
          <Typography>
            Are you sure you want to stop this subscription? It will remain
            active until{" "}
            <strong>{effectiveToDate?.format?.(dateFormat) || formatDate(subscriptionManagement?.effectiveToDate)}</strong>, and no
            further payments will be collected from your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={() => setConfirmUnsubscribeOpen(false)} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={syncing}
            onClick={() => {
              setConfirmUnsubscribeOpen(false);
              onUnsubscribe(addIn);
            }}
          >
            {syncing ? <CircularProgress size={20} /> : "Stop Subscription"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmRenewOpen}
        onClose={() => setConfirmRenewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>Renew Subscription</DialogTitle>
        <DialogContent sx={{ marginTop: 2, textAlign: "center" }}>
          <Typography>
            Renew this subscription? You will be redirected to the checkout
            flow to complete the renewal process.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={() => setConfirmRenewOpen(false)} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={syncing}
            onClick={() => {
              setConfirmRenewOpen(false);
              onRenew(addIn);
            }}
          >
            {syncing ? <CircularProgress size={20} /> : "Renew Subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

PurchaseMyAddInsDetails.propTypes = {
  addIn: PropTypes.shape({
    addInId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    vendorName: PropTypes.string,
    addInTypeText: PropTypes.string,
    addInDescription: PropTypes.string,
    ownershipType: PropTypes.string,
    enterpriseName: PropTypes.string,
    configurationRequired: PropTypes.bool,
    isConfigured: PropTypes.bool,
    subscriptionLifecycle: PropTypes.string,
    configurationDefinition: PropTypes.shape({
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          userLabel: PropTypes.string,
          inputType: PropTypes.string,
          sensitive: PropTypes.bool,
        }),
      ),
    }),
    configurations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        configurationName: PropTypes.string,
        values: PropTypes.object,
      }),
    ),
    subscription: PropTypes.shape({
      isRecurring: PropTypes.bool,
      frequencyText: PropTypes.string,
      effectiveFrom: PropTypes.string,
      effectiveTo: PropTypes.string,
      trialMessage: PropTypes.string,
      trialDurationDays: PropTypes.number,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  subscriptionManagement: PropTypes.shape({
    addInId: PropTypes.number,
    addInName: PropTypes.string,
    vendorName: PropTypes.string,
    purchaseHistoryId: PropTypes.number,
    subscriptionType: PropTypes.string,
    subscriptionFrequency: PropTypes.string,
    effectiveFromDate: PropTypes.string,
    effectiveToDate: PropTypes.string,
    hasTrial: PropTypes.bool,
    trialDurationDays: PropTypes.number,
    trialStartDate: PropTypes.string,
    trialEndDate: PropTypes.string,
    isInTrial: PropTypes.bool,
    isExpired: PropTypes.bool,
    isStopped: PropTypes.bool,
    stoppedDate: PropTypes.string,
    canRenew: PropTypes.bool,
    canStop: PropTypes.bool,
    isAutoRenewable: PropTypes.bool,
    subscriptionAmount: PropTypes.number,
    frequency: PropTypes.number,
  }),
  showRenewButton: PropTypes.bool,
  showStopButton: PropTypes.bool,
  subscriptionTypeDisplay: PropTypes.string,
  frequencyDisplay: PropTypes.string,
  effectiveFromDate: PropTypes.object,
  effectiveToDate: PropTypes.object,
  trialLabel: PropTypes.string,
  syncing: PropTypes.bool,
  dateFormat: PropTypes.string,
  onBack: PropTypes.func,
  onRenew: PropTypes.func,
  onUnsubscribe: PropTypes.func,
  onCreateConfiguration: PropTypes.func,
  onEditConfiguration: PropTypes.func,
  onDeleteConfiguration: PropTypes.func,
  accessGrantedBy: PropTypes.string,
};
